import { TRIPS } from '@/constants';
import { container } from '@/container';
import CONSTANTS from '@/helpers/constants';
import { t, te } from '@/locales';
import { PostManager } from '@/managers/PostManager';
import { TripManager } from '@/managers/TripManager';
import { UnitManager } from '@/managers/UnitManager';
import { StatusMsgContext } from '@/services/StatusMsgContext';
import { statusNames } from '@/utils/statusNames';
import { IUnitInfo } from '~/api/IUnitInfo';
import { IStatusMsg } from '~/api/messages/IStatusMsg';

// Services
const unitManager = container.get(UnitManager);
const postManager = container.get(PostManager);
const tripManager = container.get(TripManager);
const statusMsgContext = container.get(StatusMsgContext);

export interface TranslatedStatusMsg {
  code: string;
  message: string;
  success: boolean;
  original: IStatusMsg;
}

const localeRoot = 'STATUS_MSG';

const successRegExp = new RegExp(`^${localeRoot}.SUCCESS`, 'i');

const oldPrefixRegExp = /^ZOLL\.DISPATCH\.BRIDGE\.|ZOLL\.DISPATCH\.MAP\.|ZOLL\.DISPATCH\./;

// @todo Remove once all BE code has been updated with a better naming scheme.
const legacyCodeMap: Record<string, string> = {
  'UNITCANDIDATE.INVALIDTRIP': 'TRIP.UPDATE.INVALID.UNIT_CANDIDATE',
  TRIPASSIGNMENTINACTIVEVEHICLE: 'TRIP.UPDATE.INACTIVE_VEHICLE',
  TRIPASSIGNMENTINVALIDVEHICLE: 'TRIP.UPDATE.INVALID.VEHICLE',
  TRIPASSIGNMENTALREADYASSIGNED: 'TRIP.UPDATE.ALREADY_ASSIGNED',
  TRIPASSIGNMENTVEHICLENOTEXISTS: 'TRIP.UPDATE.VEHICLE_DOES_NOT_EXIST',
  TRIPUPDATEUNHANDLEDEXCEPTION: 'TRIP.UPDATE.UNHANDLED_EXCEPTION',
  TRIPUPDATEPATIENTSAVEERROR: 'TRIP.UPDATE.PATIENT_SAVE_ERROR',
  TRIPUPDATEPATIENASSIGNMENT: 'TRIP.UPDATE.PATIENT_ASSIGNMENT',
  TRIPUPDATEPATIENINFO: 'TRIP.UPDATE.PATIENT_INFO',
  TRIPUPDATEINVALIDPRIORITY: 'TRIP.UPDATE.INVALID.PRIORITY',
  TRIPUPDATEINVALIDPICKUPADDRESS: 'TRIP.UPDATE.INVALID.PICKUP_ADDRESS',
  TRIPUPDATEINVALIDNATURE: 'TRIP.UPDATE.INVALID.NATURE',
  TRIPUPDATEINVALIDCALLTYPE: 'TRIP.UPDATE.INVALID.CALL_TYPE',
  TRIPASSIGNMENTINVALIDCALLTYPEMAPPING: 'TRIP.UPDATE.INVALID.CALL_TYPE_MAPPING',
  TRIPASSIGNMENTASSIGNEDSTATUS: 'TRIP.UPDATE.ASSIGNED_STATUS',
  'CANCEL.COMPLETE': 'TRIP.UPDATE.CANCEL_COMPLETE',
  'TRIP.LEG.DATE.ERROR': 'TRIP.UPDATE.LEG_DATE_ERROR',
};

const resolveUnitId = (storedContext: unknown): string => {
  const stored = storedContext as { unitId?: string } | undefined;
  return stored?.unitId ?? '';
};

const getCustomMessage = (
  contextKey: string,
  code: string,
  storedContext?: unknown,
): string => {
  const resourceType = unitManager.isUnitConfigured ? t('unitList.unit') : t('unitList.vehicle');
  switch (contextKey) {
    case CONSTANTS.UI.CONTEXTS.UNIT_UPDATE:
    case CONSTANTS.UI.CONTEXTS.ACTIVATE_UNIT:
    case CONSTANTS.UI.CONTEXTS.DEACTIVATE_UNIT:
    case CONSTANTS.UI.CONTEXTS.SPECIAL_STATUS_UPDATE:
    case CONSTANTS.UI.CONTEXTS.UNIT_AVAILABLE_UPDATE: {
      const unitResponse = storedContext as { unit?: IUnitInfo } & IUnitInfo;
      return t(code, { resourceType, resourceName: unitResponse?.unit?.name });
    }
    case CONSTANTS.UI.CONTEXTS.CANCEL_TRIP: {
      const resourceName = unitManager.find(resolveUnitId(storedContext))?.name ?? '';
      return resourceName
        ? t(`${localeRoot}.SUCCESS.CANCEL_TRIP_ASSIGNED`, { resourceType, resourceName })
        : t(code);
    }
    case CONSTANTS.UI.CONTEXTS.DUPLICATE_TRIP_WITH_ASSIGNMENT: {
      const resourceName = unitManager.find(resolveUnitId(storedContext))?.name ?? '';
      return t(code, { resourceName });
    }
    case CONSTANTS.UI.CONTEXTS.UNASSIGN_TRIP: {
      const resourceName = (storedContext as { resourceName?: string })?.resourceName ?? '';
      // Unassigning off an inactive shift row: name the shift so the wording matches the
      // pre-assign toast instead of implying an active unit released the trip.
      const isInactiveShift = (storedContext as { isInactiveShift?: boolean })?.isInactiveShift === true;
      if (isInactiveShift && te(`${code}_INACTIVE_SHIFT`)) {
        return t(`${code}_INACTIVE_SHIFT`, { resourceType, resourceName });
      }
      return t(code, { resourceType, resourceName });
    }
    case CONSTANTS.UI.CONTEXTS.DESTINATION_MODAL:
    case CONSTANTS.UI.CONTEXTS.PENDING_ASSIGNMENTS: {
      const isInactiveShift = (storedContext as { isInactiveShift?: boolean })?.isInactiveShift === true;
      // Inactive-shift drops send the shift's vehicleId as `unitId`. `unitManager.find`
      // keys off `vehicle.vehicleId`, so a vehicle currently active on another unit
      // returns that unrelated unit — the toast then names the wrong resource
      // ("Tesla" instead of the shift's "Crenata"). Trust the caller-supplied name
      // in that case, mirroring the UNASSIGN_TRIP branch.
      const resourceName = isInactiveShift
        ? (storedContext as { resourceName?: string })?.resourceName ?? ''
        : unitManager.find(resolveUnitId(storedContext))?.name ??
          (storedContext as { resourceName?: string })?.resourceName ??
          '';
      const tripStatus = (storedContext as { tripStatus?: string })?.tripStatus;
      if (tripStatus === TRIPS.STATUS.PRE_ASSIGNED) {
        // Inactive-shift drops get their own wording so the dispatcher knows the shift
        // still has to be activated before the trip is really covered.
        const preAssignCode = isInactiveShift
          ? `${code}_PRE_ASSIGNED_INACTIVE_SHIFT`
          : `${code}_PRE_ASSIGNED`;
        if (te(preAssignCode)) {
          return t(preAssignCode, { resourceType, resourceName });
        }
      }
      return t(code, { resourceType, resourceName });
    }
    case CONSTANTS.UI.CONTEXTS.STATUS_CHANGER: {
      const payload = storedContext as {
        id?: string; status?: { id?: string }; fromStatus?: { id?: string }; fromPreAssigned?: boolean;
      };
      const statusLabel = (id?: string) => {
        const key = `statusNames.${statusNames(Number(id))}`; return te(key) ? t(key) : '';
      };
      const trip = tripManager.find(payload?.id ?? '');
      const unitName = trip?.unit?.name ?? '';
      // When a pre-assigned trip is promoted to Assigned, the unit dispatch
      // status is still '0' (Available). Show "Pre-Assigned" instead so the
      // toast accurately reflects the trip-level transition.
      let fromStatus: string;
      if (payload?.fromPreAssigned) {
        fromStatus = te('statusNames.preAssigned') ? t('statusNames.preAssigned') : '';
      }
      else {
        fromStatus = statusLabel(payload?.fromStatus?.id);
      }
      return t(code, {
        unitName,
        fromStatus,
        toStatus: statusLabel(payload?.status?.id),
      });
    }
    case CONSTANTS.UI.CONTEXTS.SPECIAL_TIMESTAMP: {
      const tripId = (storedContext as { id?: string })?.id ?? '';
      const runNumber = tripManager.find(tripId)?.runNumber ?? '';
      return t(code, { runNumber });
    }
    case CONSTANTS.UI.CONTEXTS.UNIT_POST_UPDATE:
    case CONSTANTS.UI.CONTEXTS.UNIT_AT_POST_UPDATE: {
      const payload = storedContext as { unit?: { vehicle?: { postId?: string, vehicleId?: string } } };
      const unitName = unitManager.find(payload?.unit?.vehicle?.vehicleId || '')?.name ?? '';
      const postName = postManager.find(payload?.unit?.vehicle?.postId || '')?.name ?? '';
      return t(code, { unitName, postName });
    }
    default:
      return t(code);
  }
};

export const translateStatusMsg = (original: IStatusMsg): TranslatedStatusMsg => {
  let { code } = original;
  const { contextKey } = original;
  const correlationKey = typeof original?.correlationKey === 'string' ? original.correlationKey : undefined;
  const storedContext = correlationKey ? statusMsgContext.get(correlationKey) : undefined;
  // Remove unnecessary prefixes from original status code.
  code = code.replace(oldPrefixRegExp, '');

  // Map legacy codes to new code (to match locale key path).
  if (code in legacyCodeMap) {
    code = legacyCodeMap[code];
  }

  // Prepend code with locale root.
  code = `${localeRoot}.${code}`;

  // Attempt to use qualifying context for a more specific status code.
  if (contextKey !== undefined && te(`${code}.${contextKey}`)) {
    code = `${code}.${contextKey}`;
  }
  // Use a default context if there was no context provided or there isn't a specific translation.
  else if (te(`${code}.DEFAULT`)) {
    code = `${code}.DEFAULT`;
  }

  let message: string | undefined;
  let success: boolean = false;

  // Use a specific translation for this status code.
  if (te(code)) {
    message = getCustomMessage(contextKey, code, storedContext);
    success = successRegExp.test(code);
  }
  // Otherwise, use the message that was sent.
  else {
    message = original.message;
  }

  // Optionally, you can return customResponseType for further handling
  return {
    code,
    message: message ?? '',
    success,
    original,
  };
};
